import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  analyzeBalance,
  autoRepair,
  popSnapshot,
  pushSnapshot,
  type EditSnapshot,
  type RepairLang,
} from "@roqeet/core";
import type { CodeMirrorEditorProps } from "./CodeMirrorEditor.js";
import { AiRepairPanel } from "./AiRepairPanel.js";
import type { RepairKind } from "../../state/repair-actions.js";
import { checkJsSyntax } from "../../platform/script/js-syntax.js";
import { useUiStore } from "../../state/ui-store.js";
import { useT } from "../../i18n/use-t.js";
import { Maximize2, Minimize2, Undo2, AlertTriangle, X } from "lucide-react";

/**
 * 공용 코드 에디터 — 결정 #1/#11/#30.
 *
 * 기본은 CodeMirror 6를 **lazy-load**해 구문 강조·줄번호·괄호 매칭·들여쓰기·검색을
 * 제공한다(별도 청크라 초기 번들 0 영향, 결정 #1). 로드/실행에 실패하면(저사양·구형
 * Safari·네트워크 문제) **강화된 textarea로 자동 폴백**한다 — 그래도 Tab/Enter
 * 들여쓰기·16px 자동확대 차단·전체화면이 동작한다(graceful degradation).
 *
 * CodeMirror 로드 전에도 textarea로 즉시 편집할 수 있고, 준비되면 매끄럽게 교체된다.
 */
export interface CodeEditorProps {
  value: string;
  onChange: (v: string) => void;
  /** 상단/강조용 언어 라벨("JSON" | "JavaScript" | "Lua"). */
  language?: string;
  placeholder?: string;
  /** true면 부모 flex 높이를 채운다(코드 모드). false면 minHeight 사용. */
  fill?: boolean;
  /** fill=false일 때 기본 높이(px). */
  minHeight?: number;
  /** textarea 폴백의 입력칸 배경. */
  inputBg?: string;
  /** 설정 시 "AI 도우미" 버튼을 노출하고, 이 종류로 수리/생성 루프를 돈다(결정 #30). */
  aiKind?: RepairKind;
  /** 설정 시 전체화면 헤더에 저장/자동저장 버튼을 노출한다. 현재 내용을 영속한다. */
  onSave?: () => void | Promise<void>;
}

type CMComponent = (props: CodeMirrorEditorProps) => JSX.Element;

/**
 * 에디터 언어 라벨 → 균형 검사기 문법(결정 #30). JSON은 같은 괄호 체계라 javascript로
 * 검사해도 괄호 균형이 정확하다. 알 수 없는 라벨은 null(검사 생략).
 */
function toRepairLang(language: string | undefined): RepairLang | null {
  switch ((language ?? "").toLowerCase()) {
    case "json":
    case "javascript":
    case "js":
      return "javascript";
    case "lua":
      return "lua";
    default:
      return null;
  }
}

/** <html>의 light 클래스를 관찰해 다크 여부를 반응형으로 추적(테마 전환 동기화). */
function useIsDark(): boolean {
  const [dark, setDark] = useState(
    () =>
      typeof document === "undefined" ||
      !document.documentElement.classList.contains("light"),
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(!el.classList.contains("light")));
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/** CodeMirror 모듈을 한 번만 lazy-load(여러 에디터가 공유). 실패 시 null 유지. */
let cmPromise: Promise<CMComponent | null> | null = null;
function loadCodeMirror(): Promise<CMComponent | null> {
  if (!cmPromise) {
    cmPromise = import("./CodeMirrorEditor.js")
      .then((m) => m.CodeMirrorEditor as CMComponent)
      .catch(() => null);
  }
  return cmPromise;
}

export function CodeEditor({
  value,
  onChange,
  language,
  placeholder,
  fill = false,
  minHeight = 160,
  inputBg = "bg-bg-soft",
  aiKind,
  onSave,
}: CodeEditorProps) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [CM, setCM] = useState<CMComponent | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const dark = useIsDark();

  // 전체화면 저장: onSave가 있으면 호출하고 "저장됨" 표시를 잠깐 띄운다.
  const doSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave();
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  // 편집기 확대(전체화면) 진입 시 사이드바를 자동으로 닫는다(가림 방지). 모바일
  // 드로어(sidebarOpen)와 데스크톱 고정 사이드바(sidebarCollapsed) 둘 다 처리.
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const toggleExpanded = () => {
    setExpanded((x) => {
      if (!x) {
        setSidebarOpen(false);
        setSidebarCollapsed(true);
      }
      return !x;
    });
  };

  // 경량 스냅샷(결정 #30): 코드를 통째로 교체하는 동작(자동수리·AI 적용) 직전에 현재
  // 값을 저장해, "되돌리기"로 복구할 수 있게 한다. 세션 한정(컴포넌트 수명).
  const [snapshots, setSnapshots] = useState<EditSnapshot[]>([]);
  /** 현재 값을 스냅샷으로 남기고 새 값으로 교체한다(파괴적 교체의 단일 통로). */
  const replaceWithSnapshot = (next: string, label: string) => {
    if (next === value) return;
    setSnapshots((s) => pushSnapshot(s, { value, label, at: Date.now() }));
    onChange(next);
  };
  const undo = () => {
    const r = popSnapshot(snapshots);
    if (!r) return;
    setSnapshots(r.rest);
    onChange(r.restored.value);
  };

  // 결정적 균형 검사(결정 #30): 매 입력마다 무료·즉시로 괄호/end 미닫힘을 감지.
  // 언어 라벨 → 검사기 문법. 알 수 없으면 검사 생략(이슈 바 미표시).
  const repairLang = toRepairLang(language);
  const balanceIssues = useMemo(
    () => (repairLang ? analyzeBalance(value, repairLang) : []),
    [value, repairLang],
  );

  // JS 구문 검사(Acorn, lazy-load): 균형 검사가 깨끗할 때만, 디바운스로 실행한다.
  // 역할 분담 — 괄호/문자열은 balanceIssues가, 세미콜론 등 깊은 구문 오류는 Acorn이.
  // 매 타이핑마다 파싱하지 않도록 400ms 디바운스(6s+ 보호).
  const [jsIssues, setJsIssues] = useState<string[]>([]);
  const isJs = (language ?? "").toLowerCase() === "javascript" || (language ?? "").toLowerCase() === "js";
  useEffect(() => {
    if (!isJs || balanceIssues.length > 0) {
      setJsIssues([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      void checkJsSyntax(value).then((errs) => {
        if (alive) setJsIssues(errs);
      });
    }, 400);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [value, isJs, balanceIssues.length]);

  // 표시용 통합 이슈: 균형 문제 + (있으면) JS 구문 문제.
  const issues = useMemo(
    () => [...balanceIssues, ...jsIssues.map((message) => ({ kind: "string" as const, token: "", message, line: 0, column: 0 }))],
    [balanceIssues, jsIssues],
  );

  // 자동 수리는 균형 문제가 있을 때만 계산(보수적: 닫히지 않은 여는 괄호만 EOF에 닫음).
  // JS 구문 오류(세미콜론 등)는 결정적 수리 대상이 아니라 AI 도우미로 넘긴다.
  const fix = useMemo(
    () => (repairLang && balanceIssues.length ? autoRepair(value, repairLang) : null),
    [value, repairLang, balanceIssues.length],
  );
  const canAutoFix = !!fix && fix.applied.length > 0;
  const applyFix = () => {
    if (fix) replaceWithSnapshot(fix.fixed, t("codeEd.beforeAutoRepair"));
  };

  useEffect(() => {
    let alive = true;
    void loadCodeMirror().then((c) => {
      if (alive && c) setCM(() => c);
    });
    return () => {
      alive = false;
    };
  }, []);

  // 편집 표면: CodeMirror가 준비되면 그걸로, 아니면 textarea 폴백.
  const surface = (fillFlag: boolean) =>
    CM ? (
      <CM
        value={value}
        onChange={onChange}
        language={language}
        fill={fillFlag}
        minHeight={minHeight}
        dark={dark}
      />
    ) : (
      <TextareaSurface
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        fill={fillFlag}
        minHeight={minHeight}
        inputBg={inputBg}
      />
    );

  const ExpandButton = (
    <button
      type="button"
      onClick={toggleExpanded}
      aria-label={expanded ? t("codeEd.collapseEditor") : t("codeEd.fullscreenEditor")}
      title={expanded ? t("codeEd.collapse") : t("codeEd.fullscreen")}
      className="absolute right-2 top-2 z-10 rounded-md border border-line bg-bg/80 p-1 text-ink-faint backdrop-blur hover:text-ink"
    >
      {expanded ? <Minimize2 size={16} aria-hidden="true" /> : <Maximize2 size={16} aria-hidden="true" />}
    </button>
  );

  // AI 도우미 버튼(aiKind가 있을 때만). 확장 버튼 왼쪽에 배치.
  const AiButton = aiKind ? (
    <button
      type="button"
      onClick={() => setAiOpen(true)}
      title={t("codeEd.aiHelperTitle")}
      className="absolute right-10 top-2 z-10 rounded-md border border-line bg-bg/80 px-1.5 py-1 text-xs text-accent-soft backdrop-blur hover:text-ink"
    >
      AI
    </button>
  ) : null;

  // 되돌리기 버튼: 스냅샷(자동수리·AI 적용 전)이 있을 때만 노출. AI 버튼 왼쪽.
  // 위치는 인라인 right로 고정(arbitrary 유틸 purge 위험 회피, z-index 버그와 동일 교훈).
  const UndoButton =
    snapshots.length > 0 ? (
      <button
        type="button"
        onClick={undo}
        title={t("codeEd.undoCount", { count: snapshots.length })}
        aria-label={t("codeEd.undo")}
        className="absolute top-2 z-10 flex items-center justify-center rounded-md border border-line bg-bg/80 px-1.5 py-1 text-xs text-ink-soft backdrop-blur hover:text-ink"
        style={{ right: aiKind ? 72 : 40 }}
      >
        <Undo2 size={14} aria-hidden="true" />
      </button>
    ) : null;

  const AiPanel =
    aiKind && aiOpen ? (
      <AiRepairPanel
        kind={aiKind}
        value={value}
        onApply={(next) => replaceWithSnapshot(next, t("codeEd.beforeAiApply"))}
        onClose={() => setAiOpen(false)}
      />
    ) : null;

  // 균형 문제 알림 바(결정 #30). 무료·즉시 감지 결과 + 보수적 자동수리 버튼.
  // 자동수리는 diff 없이 바로 적용하지만, '닫히지 않은 괄호 닫기'만 하므로(파괴적
  // 편집 없음) 되돌리기 부담이 작다. AI 수리(2단계)는 diff 확인을 별도로 붙인다.
  const IssueBar =
    issues.length > 0 ? (
      <div className="shrink-0 space-y-1 border-t border-line bg-bg-soft px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-amber-300">
            {t("codeEd.syntaxIssues", { count: issues.length })}
          </span>
          {canAutoFix && (
            <button
              type="button"
              onClick={applyFix}
              className="rounded-md border border-line px-2 py-0.5 text-xs text-accent-soft hover:bg-bg-raised"
              title={t("codeEd.autoRepairTitle")}
            >
              {t("codeEd.autoRepair", { count: fix!.applied.length })}
            </button>
          )}
        </div>
        <ul className="space-y-0.5">
          {issues.slice(0, 4).map((issue, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-ink-soft">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0">{issue.message}</span>
            </li>
          ))}
          {issues.length > 4 && (
            <li className="text-xs text-ink-faint">
              {t("codeEd.moreIssues", { count: issues.length - 4 })}
            </li>
          )}
        </ul>
      </div>
    ) : null;

  if (expanded) {
    return (
      <div className="fixed inset-0 flex flex-col bg-bg" style={{ zIndex: 60 }}>
        {/* 헤더: 제목 + 닫기만(액션은 하단 바로 — 폭에 상관없이 안 잘림). */}
        <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-line px-4">
          <span className="min-w-0 truncate text-sm font-medium text-ink">
            {language ? t("codeEd.editLanguage", { language }) : t("codeEd.editCode")}
            {savedAt && !saving && <span className="ml-2 text-xs text-green-400">{t("codeEd.saved")}</span>}
          </span>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label={t("codeEd.collapseEditor")}
            className="shrink-0 rounded-lg border border-line p-1 text-sm text-ink-soft hover:bg-bg-raised"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col p-3">{surface(true)}</div>
        {IssueBar}

        {/* 하단 액션 바: 전체 폭 + 줄바꿈. 어떤 화면 폭에서도 버튼이 잘리지 않는다. */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line p-3">
          {snapshots.length > 0 && (
            <button
              type="button"
              onClick={undo}
              title={t("codeEd.undoCount", { count: snapshots.length })}
              className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            >
              <Undo2 size={14} aria-hidden="true" /> {t("codeEd.undo")}
            </button>
          )}
          {aiKind && (
            <button
              type="button"
              onClick={() => setAiOpen(true)}
              className="rounded-lg border border-line px-3 py-1.5 text-sm text-accent-soft hover:bg-bg-raised"
            >
              {t("codeEd.aiHelper")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
          >
            {t("codeEd.exit")}
          </button>
          {onSave && (
            <>
              <button
                type="button"
                onClick={() => void doSave()}
                disabled={saving}
                className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
              >
                {saving ? t("common.saving") : t("common.save")}
              </button>
              <button
                type="button"
                onClick={async () => {
                  await doSave();
                  setExpanded(false);
                }}
                disabled={saving}
                className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
              >
                {t("codeEd.saveAndExit")}
              </button>
            </>
          )}
        </div>
        {AiPanel}
      </div>
    );
  }

  return (
    <div className={["relative flex flex-col", fill ? "min-h-0 flex-1" : ""].join(" ")}>
      {UndoButton}
      {AiButton}
      {ExpandButton}
      {surface(fill)}
      {IssueBar}
      {AiPanel}
    </div>
  );
}

/**
 * 강화된 textarea 폴백. CodeMirror 미로드/실패 시에도 IDE 비슷한 최소 편집을 제공:
 *  - Tab/Shift+Tab 2칸 들여쓰기/내어쓰기
 *  - Enter 이전 줄 들여쓰기 유지(+여는 괄호 뒤 한 단계 더)
 *  - fontSize 16px로 iOS 자동확대 차단(결정 #11)
 */
function TextareaSurface({
  value,
  onChange,
  placeholder,
  fill,
  minHeight,
  inputBg,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  fill: boolean;
  minHeight: number;
  inputBg: string;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (caret.current != null && ref.current) {
      ref.current.selectionStart = ref.current.selectionEnd = caret.current;
      caret.current = null;
    }
  });

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    const s = ta.selectionStart;
    const en = ta.selectionEnd;
    const v = ta.value;

    if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        const lineStart = v.lastIndexOf("\n", s - 1) + 1;
        const lead = v.slice(lineStart).match(/^ {1,2}/)?.[0].length ?? 0;
        if (lead > 0) {
          caret.current = Math.max(lineStart, s - lead);
          onChange(v.slice(0, lineStart) + v.slice(lineStart + lead));
        }
      } else {
        caret.current = s + 2;
        onChange(v.slice(0, s) + "  " + v.slice(en));
      }
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const lineStart = v.lastIndexOf("\n", s - 1) + 1;
      const indent = v.slice(lineStart, s).match(/^ */)?.[0] ?? "";
      const prev = v[s - 1];
      const extra = prev === "{" || prev === "[" || prev === "(" ? "  " : "";
      const insert = "\n" + indent + extra;
      caret.current = s + insert.length;
      onChange(v.slice(0, s) + insert + v.slice(en));
      return;
    }
  };

  return (
    <div
      className={["relative flex flex-col", fill ? "min-h-0 flex-1" : ""].join(" ")}
      style={fill ? undefined : { minHeight }}
    >
      <textarea
        ref={(el) => {
          ref.current = el;
        }}
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        style={{ fontSize: 16, lineHeight: 1.5, tabSize: 2 }}
        className={[
          "w-full resize-none rounded-lg border border-line p-3 pr-10 font-mono text-ink outline-none focus:border-accent",
          inputBg,
          fill ? "min-h-0 flex-1" : "flex-1",
        ].join(" ")}
      />
    </div>
  );
}
